import '@ta-interaktiv/semantic-ui/semantic/dist/components/button.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import React, { useContext } from 'react'
import { SelectionType } from 'types/SelectionType'
import { branch, renderComponent } from 'recompose'
import partyAbbreviations from 'lib/parties.yml'
import { getParty } from 'swiss-party-colors'
import { LocaleContext } from 'contexts/locale'

interface AnswerOptions {
  /** Any additional classes to be used in the buttons group. */
  className?: string

  /** Which option(s) currently has been selected */
  currentSelection?: Set<string> | number

  /** Handler for selecting the answer */
  handleAnswer: (answer: string | number) => void

  /** Whether the question allows multiple selection, an exclusive one – or a number */
  selectionType: SelectionType

  /** The list of possible responses */
  responses?: { [key: string]: string }
}

interface LimitedOptions extends AnswerOptions {
  currentSelection?: Set<string>
  selectionType: 'exclusive' | 'multiple' | number
  responses: { [key: string]: string }
}

interface GuessOptions extends AnswerOptions {
  currentSelection?: number
  selectionType: 'guess'
}

function cleanInput(input: string) {
  return parseInt(input.replace(/['‘’‛‚′´,.\W]/gi, ''), 10)
}

/**
 * Displays a list of options in the form of a vertical list of buttons. This
 * is functionally the same as the buttons in the questions component, without
 * the fancy colouration.
 */
export const SelectionButtons = ({
  className = '',
  responses = {},
  handleAnswer,
  currentSelection = new Set(),
  selectionType
}: LimitedOptions) => (
  <div
    className={`ui basic vertical labeled icon ${className} fluid buttons answer-buttons`}
  >
    {Object.entries(responses).map(([value, displayText]) => {
      const handler = () => handleAnswer(value)
      const iconClassNames = ['icon']
      const buttonClassNames = ['ui', 'button']

      // Change icon based on selection type
      iconClassNames.push(selectionType === 'exclusive' ? 'circle' : 'square')

      if (currentSelection.has(value)) {
        // Mark button if this has been selected
        iconClassNames.push('check')
      } else {
        iconClassNames.push('outline')

        // Additionally, check if we can select more this or not
        if (
          !isNaN(selectionType as number) &&
          currentSelection.size >= selectionType
        ) {
          buttonClassNames.push('disabled')
        }
      }

      return (
        <button
          onClick={handler}
          onKeyUp={event => {
            if (event.key === 'Enter') {
              handler()
            }
          }}
          className={buttonClassNames.join(' ')}
          key={value}
        >
          <i className={iconClassNames.join(' ')} />
          {displayText}
        </button>
      )
    })}
  </div>
)

/**
 * Displays an input field for numerical input.
 */
export const GuessInput = ({
  currentSelection,
  handleAnswer
}: GuessOptions) => (
  <div className='ui huge fluid input'>
    <input
      placeholder='178090'
      type='text'
      pattern='\d+'
      value={currentSelection || currentSelection === 0 ? currentSelection : ''}
      onChange={event => handleAnswer(cleanInput(event.target.value))}
    />
  </div>
)

export const PartyButtons = ({
  currentSelection,
  handleAnswer
}: AnswerOptions) => {
  const locale = useContext(LocaleContext)
  const translatedAbbreviations = require('locales/parties/' + locale + '.yml')

  return (
    <div className='ui basic vertical icon labeled fluid buttons'>
      {partyAbbreviations.map(abbr => {
        const party = getParty(locale, translatedAbbreviations[abbr])

        return (
          <button
            key={abbr}
            onClick={() => handleAnswer(abbr)}
            className='ui button'
            style={{
              color: party.fontColor
            }}
          >
            <i
              className={`square ${
                currentSelection && (currentSelection as Set<string>).has(abbr)
                  ? 'check'
                  : 'outline'
              } icon`}
            />
            {party.name} ({party.abbr})
          </button>
        )
      })}
    </div>
  )
}

/**
 * Shows either buttons with options or an input field for numerical input,
 * depending on the {@link SelectionType}.
 */
export const AnswerButtons = branch<AnswerOptions>(props => {
  return props.selectionType === 'guess'
}, renderComponent(GuessInput))(SelectionButtons)
